import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Icon from '../../Icons/Icon';

const CommonModal = ({
  children = null,
  handleClose = () => { },
  title = '',
  boxClassName = 'w-2/5',
  className = '',
  shadow = 'shadow-vamos-shadow',
  textAlignment = 'text-center',
  isHeaderDivider = true,
  titleStyle,
  childrenStyle,
  titlePosition = 'center', // Default value, if need to move header to left, pass 'left'
  closeIconPosition = 'absolute right-6 top-8', // Default value, if need close icon for modal with header on the left, pass empty string ''
  headerClassName = '',
  maxHeight = 'max-h-[90vh]',
  titleClassName = '',
  minWidth = 'min-w-[420px]',
  overflowY = 'overflow-y-auto',
  childrenHeight = 'max-h-[75vh]',
  dataTestId = undefined,
}) => {
  const modalRef = useRef(null);
  const mouseDownOnBackdrop = useRef(false);

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  const handleBackdropMouseDown = (e) => {
    mouseDownOnBackdrop.current = e.target === e.currentTarget;
  };

  const handleBackdropClick = (e) => {
    if (mouseDownOnBackdrop.current && e.target === e.currentTarget) {
      handleClose();
    }
    mouseDownOnBackdrop.current = false;
  };

  const handleTitlePosition = {
    left: 'items-center flex place-content-between',
    center: 'text-center',
  };

  return (
    <div
      ref={modalRef}
      className="fixed top-0 left-0 bg-modal-background z-[99] w-screen h-screen flex items-center justify-center"
      onClick={handleBackdropClick}
      onMouseDown={handleBackdropMouseDown}
      onKeyDown={(e) => { if (e.key === 'Escape') handleClose(); }} // Handle escape key
      role="button" // Set an interactive role, but choose appropriately based on actual usage
      aria-label="Close modal" // Provide accessible label
      tabIndex="0" // Make the div focusable
    >
      <div
        className={`${overflowY} ${boxClassName} bg-white rounded-md ${maxHeight} ${minWidth} relative ${shadow} ${className} cursor-default`}
        onClick={(e) => e.stopPropagation()} // Prevent click events from bubbling
        role="presentation" // Indicate this is for presentation only
      >
        <div className={`${handleTitlePosition[titlePosition]} ${headerClassName} ${isHeaderDivider ? 'border-b border-vamos-light-gray' : ''}`}>
          <h2 className={`${titleStyle || 'text-2xl font-bold'} ${titleClassName}`} data-testid={dataTestId}>{title}</h2>

          <button
            type="button"
            onClick={handleClose}
            aria-label="Close" // Accessibility label
            className={`w-6 h-6 rounded-full hover:bg-vamos-blue-gray justify-items-center outline-none z-10 ${closeIconPosition}`}
          >
            <Icon name="close_modal" />
          </button>
        </div>
        <div className={`${textAlignment} flex-column relative ${childrenHeight} ${childrenStyle || ''}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

CommonModal.propTypes = {
  children: PropTypes.node,
  handleClose: PropTypes.func,
  title: PropTypes.string,
  boxClassName: PropTypes.string,
  className: PropTypes.string,
  shadow: PropTypes.string,
  textAlignment: PropTypes.string,
  isHeaderDivider: PropTypes.bool,
  titleStyle: PropTypes.string,
  childrenStyle: PropTypes.string,
  titlePosition: PropTypes.string,
  closeIconPosition: PropTypes.string,
  headerClassName: PropTypes.string,
  maxHeight: PropTypes.string,
  titleClassName: PropTypes.string,
  minWidth: PropTypes.string,
  overflowY: PropTypes.string,
  childrenHeight: PropTypes.string,
  dataTestId: PropTypes.string,
};

export default CommonModal;
